import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import ContainerForm from 'components/ContainerForm';
import { InputTitle } from 'components/input/InputTitle';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import {
  Checkbox,
  FileUpload,
  Radio,
  Row,
  Select,
  Spacing,
  TextInput,
  Typography,
  closeModal,
  openModal,
} from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { BorderContainer } from 'modules/dashboard/styles/common';

import { CustomFileUpload } from '../styles/dashboard';
import { AddButton, CustomEditor, CustomSelect, LoggingMetric } from '../styles/functional-test';

import { ReactComponent as AddIcon } from 'assets/icons/add.svg';
import { ReactComponent as TooltipIcon } from 'assets/icons/tooltip.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { Footer } from '../components/Footer';
import { HeaderDetailPage } from '../components/HeaderDetailPage';
import { ScoringRuleTable } from '../components/ScoringRuleTable';
import { Video } from '../components/Video';
import { useFunctionalTestFormController } from '../core/controller/functional-test-form';
import { FunctionalTestMethod, FunctionalTestType } from '../core/enum';
import { metricMenu } from '../core/select';

const AddFunctionalTestPage = () => {
  const {
    goBack,
    onChangeType,
    videoUrl,
    thumbnailUrl,
    handleRemoveThumbnail,
    handleRemoveVideo,
    onChangeThumbnail,
    onChangeVideo,
    handleChange,
    handleSubmit,
    disabled,
    disabledButton,
    onCancel,
    values,
    functionalTestId,
    functionalTestDetail,
    onChangeMetric,
    handleAddScoreRule,
    handleRemoveScoreRule,
    selectedData,
    onChangeValueScoreRules,
    onChangeProtocol,
    protocol,
    onDeleteFunctionalTest,
    showScoreRules,
    showScoringType,
    onChangeSwitch,
    showSpO2AndHR,
    hideMaxScoreAndThresholdTime,
    handleGetWidthAndHeightThumbnail,
    handleGetWidthAndHeightVideo,
    onChangeAddition,
    isFunctionalTestDetailEqual,
  } = useFunctionalTestFormController();

  const selectedKey = Object.keys(FunctionalTestMethod).filter(
    (el) => values?.method?.toLowerCase() === el.toLowerCase(),
  );

  const handleKeyPress = (e: any) => {
    const keyCode = e.keyCode || e.which;
    const keyValue = String.fromCharCode(keyCode);

    if ((e.target.value === '' && keyValue === '0') || keyValue === '-') {
      e.preventDefault();
    }
  };

  return (
    <ContainerForm
      onCancel={onCancel}
      onSubmit={handleSubmit}
      disabled={functionalTestId ? disabledButton : disabled}
      titleSubmit={functionalTestId ? 'Save' : 'Add'}
      titleCancel={functionalTestId && 'Cancel changes'}
      disabledCancel={functionalTestId ? isFunctionalTestDetailEqual : false}
    >
      <HeaderDetailPage
        title={functionalTestId ? String(functionalTestDetail?.name) : 'Add functional test'}
        showAction={Boolean(functionalTestId)}
        actions={[
          {
            label: 'Delete',
            onClick: () => openModal(MODAL_KEY.DELETE_FUNCTIONAL_TEST),
          },
        ]}
        goBack={goBack}
      />

      <BorderContainer>
        <Spacing spacing="xl" placement="bottom" type="margin" className="max-w-800">
          <Typography
            style={{ marginBottom: 24 }}
            size={20}
            weight={500}
            height={28}
            color={COLOR.surfaceNeutralBold['neutral-11']}
          >
            Test details
          </Typography>
          <InputTitle
            title={
              <Typography size={14} weight={600} height={20} color={COLOR_CONFIG['neutral-text']}>
                Exercise name
              </Typography>
            }
          />
          <TextInput
            className="max-w-800"
            name="name"
            value={values.name}
            onChange={handleChange}
            maxLength={80}
          />
        </Spacing>

        <Spacing
          spacing="xl"
          placement="bottom"
          type="margin"
          style={{
            marginBottom: showScoringType ? 24 : 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridGap: 12,
            width: '100%',
            maxWidth: 800,
          }}
        >
          <LoggingMetric>
            <InputTitle
              title={
                <Typography size={14} weight={600} height={20} color={COLOR_CONFIG['neutral-text']}>
                  Logging metric
                </Typography>
              }
            />
            <CustomSelect>
              <Select
                inputStyle={{
                  width: '100%',
                  borderRadius: 12,
                  background: COLOR_CONFIG.white,
                  maxWidth: 259,
                }}
                containerStyle={{ maxWidth: 'none' }}
                options={metricMenu}
                selectedKeys={selectedKey}
                onChange={onChangeMetric}
                dropdownConfig={{
                  dropdownConfig: {
                    placement: 'bottom-start',
                    offset: [-10, 4],
                  },
                }}
                placeholder="Select metric"
                activeValue={
                  values.method === Object.keys(FunctionalTestMethod)[0]
                    ? 'Rep count entry'
                    : values.method === Object.keys(FunctionalTestMethod)[1]
                      ? 'Score entry'
                      : values.method === Object.keys(FunctionalTestMethod)[2]
                        ? 'Time entry'
                        : values.method === Object.keys(FunctionalTestMethod)[3]
                          ? 'Weight entry'
                          : ''
                }
                disabled={functionalTestId ? true : false}
              />
            </CustomSelect>
          </LoggingMetric>

          {hideMaxScoreAndThresholdTime ? null : (
            <>
              <div>
                <InputTitle title="Max score" />
                <TextInput
                  name="maxScore"
                  containerStyle={{ maxWidth: 259, width: '100%' }}
                  inputStyle={{
                    borderRadius: 12,
                  }}
                  type="number"
                  value={values.maxScore}
                  onChange={handleChange}
                  min={1}
                  onKeyPress={handleKeyPress}
                />
              </div>
              <div>
                <InputTitle
                  title="Threshold time (s) (optional)"
                  icon={<TooltipIcon />}
                  contentTooltip={
                    <p style={{ textAlign: 'left' }}>
                      This allows you set an auto stop time for all stopwatches in Exercise Details
                      screen in mobile app.
                    </p>
                  }
                />
                <TextInput
                  name="thresholdTime"
                  containerStyle={{ maxWidth: 259, width: '100%' }}
                  inputStyle={{
                    borderRadius: 12,
                  }}
                  value={values.thresholdTime}
                  onChange={handleChange}
                  maxLength={80}
                  suffix="second"
                  type="number"
                  min={1}
                />
              </div>
            </>
          )}
        </Spacing>

        {showScoringType && (
          <Spacing spacing="xl" placement="bottom" type="margin">
            <InputTitle
              title={
                <Typography size={14} height={20} weight={600} color={COLOR_CONFIG['neutral-text']}>
                  Scoring method
                </Typography>
              }
              icon={<TooltipIcon />}
              contentTooltip={
                <div>
                  <ul>
                    <li style={{ textAlign: 'left' }}>
                      Manual scoring : <br />
                      Select this to manually enrer the score.
                    </li>
                    <li style={{ textAlign: 'left' }}>
                      Auto scoring : <br />
                      Select this if the score is automatically calculated based on finish time.
                    </li>
                  </ul>
                </div>
              }
            />
            <Row>
              {Object.keys(FunctionalTestType).map((key) => {
                const checked = values?.type?.toLowerCase() === key.toLowerCase();

                return (
                  <Radio
                    key={key}
                    checked={checked}
                    onClick={onChangeType(key)}
                    style={{
                      marginRight: 10,
                    }}
                  >
                    <Typography
                      size={16}
                      height={22}
                      color={COLOR.surfaceNeutralBold['neutral-11']}
                    >
                      {key}
                    </Typography>
                  </Radio>
                );
              })}
            </Row>
          </Spacing>
        )}

        {showSpO2AndHR && (
          <div className="mt-24">
            <Typography size={14} height={20} weight={600} color={COLOR_CONFIG['neutral-text']}>
              Addition
            </Typography>

            <Checkbox
              style={{ paddingTop: 2 }}
              onClick={onChangeSwitch}
              checked={values.isSO2AndHR}
            >
              <Typography size={16} height={22} color={COLOR.surfaceNeutralBold['neutral-11']}>
                Include fields to record SpO2 and heart rate.
              </Typography>
            </Checkbox>
          </div>
        )}

        {showScoringType && (
          <Row direction="column">
            <InputTitle
              title={
                <Typography size={14} height={20} weight={600} color={COLOR_CONFIG['neutral-text']}>
                  Addition
                </Typography>
              }
            />
            <div className="flex items-center">
              <Checkbox
                style={{ paddingTop: 2 }}
                onClick={onChangeAddition}
                checked={values.isAddition}
              ></Checkbox>
              <Typography size={16} height={22} color={COLOR.surfaceNeutralBold['neutral-11']}>
                Include 30-second Chair Stand test
              </Typography>
            </div>

            <Typography
              style={{ marginLeft: 32 }}
              size={14}
              height={20}
              color={COLOR_CONFIG['neutral-text']}
            >
              (this is for combining the 5-rep Chair Stand and 30-second Chair Stand tests)
            </Typography>
          </Row>
        )}
      </BorderContainer>

      {showScoreRules && (
        <BorderContainer className="mt-24">
          <>
            <Typography
              size={20}
              weight={500}
              height={28}
              color={COLOR.surfaceNeutralBold['neutral-11']}
            >
              Scoring rule
            </Typography>
            <ScoringRuleTable
              scoringRules={selectedData}
              onChangeValueSelectedFunctionalTest={onChangeValueScoreRules}
              onDelete={handleRemoveScoreRule}
            />
            <AddButton onClick={handleAddScoreRule} style={{ margin: '24px 0 0' }}>
              <AddIcon style={{ width: 20, height: 20 }} />
              <Typography size={14} height={20} weight={600} color={COLOR.purpel['purpel-6']}>
                Add rule
              </Typography>
            </AddButton>
          </>
        </BorderContainer>
      )}

      <BorderContainer style={{ marginTop: 24 }}>
        <Typography
          size={20}
          weight={500}
          height={28}
          color={COLOR.surfaceNeutralBold['neutral-11']}
        >
          Test resources
        </Typography>

        <Video
          videoUrl={videoUrl.value}
          handleGetWidthAndHeightVideo={handleGetWidthAndHeightVideo}
          handleRemoveVideo={handleRemoveVideo}
          onChangeVideo={onChangeVideo}
        />

        <Spacing spacing="xl" placement="bottom" type="margin">
          <InputTitle
            title={
              <Typography size={14} height={20} weight={600} color={COLOR_CONFIG['neutral-text']}>
                Thumbnail (optional)
              </Typography>
            }
          />

          {thumbnailUrl.value && !thumbnailUrl.value.includes('thumbnail-default') ? (
            <div>
              <img
                onLoad={handleGetWidthAndHeightThumbnail}
                src={thumbnailUrl.value}
                alt="thumbnail"
                style={{
                  border: `1px solid ${COLOR_CONFIG['dwarven-bronze']}`,
                  borderRadius: 12,
                  maxWidth: 240,
                  maxHeight: 240,
                }}
              />
              <Typography
                variant="body-3"
                color={COLOR_CONFIG.red}
                style={{
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
                onClick={handleRemoveThumbnail}
              >
                Remove thumbnail
              </Typography>
            </div>
          ) : (
            <CustomFileUpload>
              <FileUpload
                id={'thumbnail'}
                icon={<UploadIcon />}
                title="Click or drag and drop to upload"
                onChangeFiles={onChangeThumbnail}
                accept=".png,.jpeg,.svg,.webp"
                style={{ height: 107 }}
              />
            </CustomFileUpload>
          )}
        </Spacing>

        <InputTitle
          title={
            <Typography size={14} height={20} weight={600} color={COLOR_CONFIG['neutral-text']}>
              Protocol (optional)
            </Typography>
          }
        />
        <CustomEditor>
          <Editor
            editorState={protocol}
            toolbarClassName="toolbarClassName"
            wrapperClassName="wrapperClassName"
            editorClassName="editorClassName"
            onEditorStateChange={onChangeProtocol}
            toolbar={{
              options: ['inline', 'list', 'image', 'fontSize'],
              inline: {
                options: ['bold', 'italic', 'underline'],
              },
              fontSize: {
                options: [12, 14, 16, 18, 20, 24, 28, 32],
              },
            }}
            editorStyle={{
              maxWidth: 800,
              position: 'relative',
              zIndex: 0,
            }}
          />
        </CustomEditor>
      </BorderContainer>

      <ConfirmModal
        modalKey={MODAL_KEY.DELETE_FUNCTIONAL_TEST}
        content={'Deleting will also remove them from all existing functional test.'}
        header={`Delete functional test`}
        onCancel={() => closeModal(MODAL_KEY.DELETE_FUNCTIONAL_TEST)}
        onSubmit={onDeleteFunctionalTest}
      />
    </ContainerForm>
  );
};

export default AddFunctionalTestPage;
